import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Lock, User2, Loader2, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

export const AuthModal = () => {
  const { authOpen, setAuthOpen, authMode, setAuthMode, login, register, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const a = t.auth;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authOpen) {
      setError(null); setLoading(false);
    }
  }, [authOpen]);

  if (!authOpen) return null;

  const isLogin = authMode === "login";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      if (isLogin) await login({ email, password });
      else await register({ email, password, name });
      setAuthOpen(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      setError(err.message || a.error_generic);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center px-4"
        data-testid="auth-modal"
      >
        <div
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={() => setAuthOpen(false)}
        />
        <motion.div
          initial={{ y: 16, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -8, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-950 to-black/90 p-7 shadow-[0_0_60px_rgba(255,140,0,0.18)] backdrop-blur-xl"
        >
          {/* Top accent line */}
          <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/70 to-transparent" />

          <button
            onClick={() => setAuthOpen(false)}
            className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/60 transition-all hover:border-white/30 hover:text-white"
            data-testid="auth-close-btn"
            aria-label={a.close}
          >
            <X className="h-4 w-4" />
          </button>

          <div className="mb-5">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-400/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-orange-200">
              <Sparkles className="h-3 w-3" />
              RE₿EL · {isLogin ? "ACCESS" : "JOIN"}
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight text-white md:text-3xl">
              {isLogin ? a.login_title : a.register_title}
            </h2>
            <p className="mt-1 text-sm text-white/55">
              {isLogin ? a.login_sub : a.register_sub}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-3">
            {/* Google OAuth button */}
            <button
              type="button"
              onClick={loginWithGoogle}
              data-testid="auth-google-btn"
              className="group relative flex w-full items-center justify-center gap-3 overflow-hidden rounded-xl border border-white/15 bg-white/[0.04] px-5 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:border-white/35 hover:bg-white/[0.08]"
            >
              <GoogleGlyph />
              {a.google_btn}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/15 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            </button>

            {/* Divider */}
            <div className="relative flex items-center py-1">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
              <span className="px-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
                {a.or_divider}
              </span>
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
            </div>

            {!isLogin && (
              <Field
                icon={<User2 className="h-4 w-4 text-white/40" />}
                type="text" placeholder={a.name} value={name}
                onChange={(e) => setName(e.target.value)}
                testid="auth-name-input"
              />
            )}
            <Field
              icon={<Mail className="h-4 w-4 text-white/40" />}
              type="email" placeholder={a.email} value={email}
              onChange={(e) => setEmail(e.target.value)} required
              testid="auth-email-input"
            />
            <Field
              icon={<Lock className="h-4 w-4 text-white/40" />}
              type="password" placeholder={a.password} value={password}
              onChange={(e) => setPassword(e.target.value)} required
              minLength={6}
              testid="auth-password-input"
            />

            {error && (
              <div className="rounded-lg border border-red-400/30 bg-red-400/5 px-3 py-2 text-xs text-red-300" data-testid="auth-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !password}
              data-testid={isLogin ? "auth-login-submit" : "auth-register-submit"}
              className="group relative mt-1 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-3 text-xs font-black uppercase tracking-[0.25em] text-black shadow-[0_0_24px_rgba(255,140,0,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {isLogin ? a.submit_login : a.submit_register}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            </button>

            <button
              type="button"
              onClick={() => { setError(null); setAuthMode(isLogin ? "register" : "login"); }}
              className="block w-full text-center text-xs text-white/50 transition-colors hover:text-white"
              data-testid="auth-switch-mode"
            >
              {isLogin ? a.switch_to_register : a.switch_to_login}
            </button>
          </form>

          {!isLogin && (
            <p className="mt-5 border-t border-white/5 pt-4 text-center text-[10px] uppercase tracking-[0.2em] text-white/35">
              ✦ {a.free_perks}
            </p>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const Field = ({ icon, testid, ...rest }) => (
  <label className="relative flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2.5 transition-colors focus-within:border-orange-400/50 focus-within:bg-white/[0.06]">
    {icon}
    <input
      data-testid={testid}
      className="flex-1 bg-transparent text-sm text-white placeholder-white/30 outline-none"
      {...rest}
    />
  </label>
);

const GoogleGlyph = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.5 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.9 29.3 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.4-11.3-8.1l-6.5 5C9.6 39.7 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4 5.6l6.3 5.3C41.8 35.3 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/>
  </svg>
);

export default AuthModal;
